import React, { useState, useEffect, useRef } from 'react';
import { Link, withPrefix } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { useScrollLock } from '../../hooks/useScrollLock';
import { SNS_LINKS } from '../../domain/consultation-counter/constants';
import NavigationButton from '../common_ui/NavigationButton';
import HamburgerMenu from './HamburgerMenu';
import * as styles from './Header.module.css';
import { Link as GatsbyLink } from 'gatsby';
import Image from '../common_ui/Image';

export interface SubNavigationButtonProps {
  children: React.ReactNode;
  to: string;
}

const SubNavigationButton: React.FC<SubNavigationButtonProps> = ({
  children,
  to,
}) => {
  return (
    <GatsbyLink to={to} className={styles.subNavButton}>
      <span className={styles.subNavButtonContent}>
        {children}
        <span className={styles.subNavLinkArrow} aria-hidden='true'>
          <Image
            src='/svg/arrow_right.svg'
            alt=''
            className={styles.navButtonArrowIcon}
          />
        </span>
      </span>
    </GatsbyLink>
  );
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const mobileNavRef = useRef<HTMLElement>(null);
  const compactNavRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // ハンバーガーメニューが開いている時にスクロールを制御
  useScrollLock(isMenuOpen);

  // 画面サイズの変更を監視して、デスクトップサイズになった時にメニューを閉じる
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        mobileNavRef.current &&
        compactNavRef.current &&
        hamburgerRef.current &&
        !mobileNavRef.current.contains(event.target as Node) &&
        !compactNavRef.current.contains(event.target as Node) &&
        !hamburgerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.container}>
          <Link to='/' className={styles.logo} aria-label='ホームページへ戻る'>
            <Image
              src='/svg/logo_on_navigation.svg'
              alt='プレコンセプションケア'
            />
          </Link>

          {/* デスクトップ用ナビゲーション（常時表示） */}
          <nav className={styles.nav}>
            <div className={styles.subNavButtonList}>
              <SubNavigationButton to='/jichitai'>
                自治体の方へ
              </SubNavigationButton>
              <SubNavigationButton to='/iryou'>
                医療機関の方へ
              </SubNavigationButton>
              <SubNavigationButton to='/precon-supporter'>
                プレコンサポーターの方へ
              </SubNavigationButton>
              <SubNavigationButton to='/shizai'>
                プレコン普及啓発資材
              </SubNavigationButton>
            </div>
            <div className={styles.navContent}>
              <div className={styles.navButtons}>
                <NavigationButton
                  to='/#about'
                  label='プレコンセプションケアとは？'
                  className={styles.navButton}
                />
                <NavigationButton
                  to='/manga'
                  label='マンガでプレコン！'
                  className={styles.navButton}
                />
                <NavigationButton
                  to='/dictionary'
                  label='プレコン Dictionary'
                  className={styles.navButton}
                />
                <NavigationButton
                  to='/soudan'
                  label='相談窓口一覧'
                  className={styles.navButton}
                />
                <NavigationButton
                  to='/event'
                  label='シンポジウム・イベント情報'
                  className={styles.navButton}
                />
                <NavigationButton
                  to='/contact'
                  label='お問い合わせ'
                  className={styles.navButton}
                />
              </div>

              {/* ハンバーガーメニュー */}
              <button
                ref={hamburgerRef}
                className={styles.hamburger}
                onClick={toggleMenu}
                aria-label={isMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
                aria-expanded={isMenuOpen}
                aria-controls='mobile-nav'
              >
                {isMenuOpen ? (
                  <Image src='/svg/close.svg' alt='' />
                ) : (
                  <Image src='/svg/density_medium.svg' alt='' />
                )}
              </button>

              <div className={styles.divider}>
                <Image src='/svg/divider.svg' alt='' />
              </div>

              <div className={styles.socialContainer}>
                <a
                  href={SNS_LINKS.instagram}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={styles.socialButton}
                  aria-label='Instagramを開く（新しいタブ）'
                >
                  <div className={styles.socialIcon}>
                    <StaticImage
                      src='../../../static/img/instagram.png'
                      alt='Instagram'
                      placeholder='blurred'
                    />
                  </div>
                </a>
                <a
                  href={SNS_LINKS.youtube}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={styles.socialButton}
                  aria-label='YouTubeを開く（新しいタブ）'
                >
                  <div className={styles.socialIcon}>
                    <StaticImage
                      src='../../../static/img/youtube.png'
                      alt='YouTube'
                      placeholder='blurred'
                    />
                  </div>
                </a>
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* モバイル用ドロワーナビゲーション */}
      <HamburgerMenu
        isMenuOpen={isMenuOpen}
        onToggleMenu={toggleMenu}
        mobileNavRef={mobileNavRef}
        compactNavRef={compactNavRef}
      />
    </>
  );
};

export default Header;
